import { useEffect, useMemo, useRef, useState } from 'react'
import { heatmapDays } from '../data/trackers'
import { formatDayLabel } from '../lib/format'
import { shiftDayKey, type DayKey } from '../lib/time'

interface Props {
  from: DayKey
  to: DayKey
  counts: Map<DayKey, number>
  color: string
  unit: string | null
  selected?: DayKey | null
  onSelect?: (day: DayKey) => void
}

const GAP = 3
const MIN_CELL = 9
const MAX_CELL = 20

/**
 * A year at a glance: one square per day, a column per week. The point of the
 * whole tracker is being able to see *when*, so this is the primary view and the
 * row of buttons at the top is the secondary one.
 *
 * Cells are sized from the width available rather than fixed, so the same
 * component reads on a phone and fills a desktop card.
 */
export function Heatmap({ from, to, counts, color, unit, selected, onSelect }: Props) {
  const cells = useMemo(() => heatmapDays(from, to), [from, to])
  const weeks = Math.ceil(cells.length / 7)

  const wrapRef = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState(0)
  useEffect(() => {
    const node = wrapRef.current
    if (!node) return
    const measure = () => setWidth(node.clientWidth)
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(node)
    return () => observer.disconnect()
  }, [])

  const cell =
    width > 0
      ? Math.min(MAX_CELL, Math.max(MIN_CELL, Math.floor((width - (weeks - 1) * GAP) / weeks)))
      : MIN_CELL

  const peak = useMemo(
    () => [...counts.values()].reduce((max, count) => Math.max(max, count), 0),
    [counts],
  )

  // A month label sits over the column its first day falls in.
  const months = useMemo(() => {
    const found: Array<{ column: number; label: string }> = []
    cells.forEach((day, index) => {
      if (day === null || index % 7 !== 0) return
      const label = formatDayLabel(day, 'MMM')
      if (formatDayLabel(shiftDayKey(day, -7), 'MMM') === label) return
      found.push({ column: index / 7, label })
    })
    return found
  }, [cells])

  return (
    <div ref={wrapRef} className="min-w-0 overflow-x-auto pb-1">
      <div style={{ width: weeks * (cell + GAP) - GAP }}>
        <div className="relative h-4" aria-hidden="true">
          {months.map((month) => (
            <span
              key={`${month.column}-${month.label}`}
              className="absolute top-0 text-[11px] text-faint"
              style={{ left: month.column * (cell + GAP) }}
            >
              {month.label}
            </span>
          ))}
        </div>
        <div className="grid grid-flow-col" style={{ gridTemplateRows: `repeat(7, ${cell}px)`, gap: GAP }}>
          {cells.map((day, index) => {
            if (day === null) return <span key={`pad-${index}`} aria-hidden="true" />
            const count = counts.get(day) ?? 0
            const share = peak > 0 ? count / peak : 0
            const amount =
              count === 0 ? 'nothing' : `${count}${unit ? ` ${unit}${count === 1 ? '' : 's'}` : ''}`
            const title = `${formatDayLabel(day, 'EEE d MMM')} — ${amount}`
            return (
              <button
                key={day}
                type="button"
                title={onSelect ? `${title} · pick it to edit` : title}
                aria-label={title}
                aria-pressed={day === selected}
                onClick={() => onSelect?.(day)}
                className={`rounded-[3px] transition-transform duration-150 hover:scale-125 ${
                  day === selected ? 'ring-2 ring-text' : ''
                }`}
                style={{
                  width: cell,
                  height: cell,
                  backgroundColor:
                    count === 0
                      ? 'var(--tg-raised)'
                      : `color-mix(in oklab, ${color} ${Math.round(32 + share * 68)}%, var(--tg-surface))`,
                }}
              />
            )
          })}
        </div>
      </div>
    </div>
  )
}
